import { useCallback, useEffect, useState } from "react";
import { Button, Card, StatusIndicator } from "./ui";
import {
  ApiError,
  api,
  type DiscordPermissionConsent,
  type DiscordPermissionTier,
} from "../lib/api";
import { formatDateNumeric } from "../lib/format";

/**
 * "What ODIN can do in your server" — the permission consent panel.
 *
 * WHY THIS EXISTS
 * ODIN's install link used to request permission bitfield `0`. Discord accepted
 * it, the customer watched ODIN appear in their member list, and ODIN could not
 * create a channel, assign a role or post a message. Nothing said so. This panel
 * is the other half of the fix: the customer is told, in words, what ODIN is
 * about to ask their server for, before they are sent to Discord.
 *
 * WHAT IT REFUSES TO DO
 *  - It never preselects Administrator. STANDARD is the default and the
 *    recommendation, and the copy says most servers never change it.
 *  - It never sends a permission bitfield. The request body carries a tier NAME.
 *    A panel that posted an integer could be made to ask for Administrator by
 *    anything that could reach the API, without a human ever seeing the risks.
 *  - It never enables the Administrator button until the risk list has been
 *    acknowledged, and the acknowledgement resets whenever the panel reloads.
 *    An accidental double-click cannot escalate a server.
 *  - It never hides the way back. Returning to the standard permissions is one
 *    button with no confirmation, because the safe direction must be easier than
 *    the risky one (Part 15: no dark patterns).
 *  - It never tells a viewer they did something wrong. A member who cannot change
 *    the setting still sees the whole list; the controls are simply absent, with
 *    a line saying who can change it.
 */

const TIER_LABEL: Record<DiscordPermissionTier, string> = {
  STANDARD: "Standard ODIN permissions",
  ADMINISTRATOR: "Administrator",
};

/** Discord's own constant names, rendered as something a person can read. */
export function humanisePermission(name: string): string {
  return name
    .toLowerCase()
    .split("_")
    .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: DiscordPermissionConsent }
  | { kind: "error"; message: string };

export function DiscordPermissionsPanel({
  tenantId,
  onChanged,
}: {
  tenantId: string;
  onChanged?: (consent: DiscordPermissionConsent) => void;
}): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [expanded, setExpanded] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await api.discordPermissions.get(tenantId);
      setState({ kind: "ready", data });
      // The acknowledgement is per-visit on purpose. It is a statement about
      // what this person has just read, not a stored preference.
      setAcknowledged(false);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "We could not load ODIN's Discord permissions.";
      setState({ kind: "error", message });
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const choose = async (tier: DiscordPermissionTier): Promise<void> => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const data = await api.discordPermissions.choose(tenantId, tier, acknowledged);
      setState({ kind: "ready", data });
      setAcknowledged(false);
      setShowAdmin(false);
      setNotice(
        tier === "ADMINISTRATOR"
          ? "Saved. Reinstall ODIN in Discord, or grant it the Administrator role there, for this to take effect on a server that is already connected."
          : "Saved. ODIN will ask for the standard permissions from now on.",
      );
      onChanged?.(data);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "We could not save that. Nothing has changed.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (state.kind === "loading") {
    return (
      <Card title="What ODIN can do in your server">
        <p className="card__desc">Loading…</p>
      </Card>
    );
  }

  if (state.kind === "error") {
    return (
      <Card title="What ODIN can do in your server">
        <StatusIndicator tone="warn" label="Could not load" />
        <p className="card__desc">{state.message}</p>
        <Button variant="secondary" type="button" onClick={() => void load()}>
          Try again
        </Button>
      </Card>
    );
  }

  const { data } = state;
  const isAdmin = data.tier === "ADMINISTRATOR";

  return (
    <Card
      title="What ODIN can do in your server"
      description="ODIN asks Discord for exactly these permissions when you install it. Nothing here is hidden, and you can change it at any time."
    >
      <p>
        <StatusIndicator
          tone={isAdmin ? "warn" : "ok"}
          label={`Current: ${TIER_LABEL[data.tier]}`}
        />
      </p>

      {isAdmin && data.acknowledgedAt ? (
        <p className="card__desc">
          Administrator was granted on {formatDateNumeric(new Date(data.acknowledgedAt))}.
        </p>
      ) : null}

      <p className="card__desc">{data.catalogue.standardRationale}</p>

      <ul>
        {data.catalogue.groups.map((group) => (
          <li key={group.id}>
            <strong>{group.title}</strong>
            <span> — {group.summary}</span>
            {expanded ? (
              <>
                <p className="card__desc">Without it: {group.withoutIt}</p>
                <p className="card__desc">
                  {group.permissions.map(humanisePermission).join(", ")}
                </p>
              </>
            ) : null}
          </li>
        ))}
      </ul>

      <Button
        variant="ghost"
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? "Hide the exact permissions" : "Show the exact permissions"}
      </Button>

      {expanded ? (
        <section>
          <h4>What ODIN deliberately does not ask for</h4>
          <ul>
            {data.catalogue.declined.map((item) => (
              <li key={item.permission}>
                <strong>{humanisePermission(item.permission)}</strong> — {item.reason}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {notice ? <p role="status">{notice}</p> : null}
      {error ? <p role="alert">{error}</p> : null}

      {!data.canChange ? (
        <p className="card__desc">
          A workspace owner or admin can change what ODIN is allowed to do.
        </p>
      ) : null}

      {data.canChange && isAdmin ? (
        <Button variant="secondary" type="button" disabled={saving} onClick={() => void choose("STANDARD")}>
          {saving ? "Saving…" : "Return to the standard permissions"}
        </Button>
      ) : null}

      {data.canChange && !isAdmin && data.canChooseAdministrator ? (
        <section>
          <Button
            variant="ghost"
            type="button"
            aria-expanded={showAdmin}
            onClick={() => {
              setShowAdmin((v) => !v);
              setAcknowledged(false);
            }}
          >
            {showAdmin ? "Never mind" : "Give ODIN the Administrator permission instead"}
          </Button>

          {showAdmin ? (
            <div role="group" aria-label="Administrator risk warning">
              <StatusIndicator tone="danger" label="Read this before you grant Administrator" />
              <ul>
                {data.catalogue.administratorRisks.map((risk) => (
                  <li key={risk}>{risk}</li>
                ))}
              </ul>
              <label>
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />{" "}
                I have read what Administrator means and I want to grant it anyway.
              </label>
              <Button
                variant="primary"
                type="button"
                disabled={!acknowledged || saving}
                onClick={() => void choose("ADMINISTRATOR")}
              >
                {saving ? "Saving…" : "Grant Administrator"}
              </Button>
            </div>
          ) : null}
        </section>
      ) : null}

      {data.canChange && !isAdmin && !data.canChooseAdministrator ? (
        <p className="card__desc">
          Only a workspace owner or admin can give ODIN the Administrator permission.
        </p>
      ) : null}
    </Card>
  );
}
